import React, { useEffect, useState } from 'react';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../../../shared/storage/firebase';
import { User } from '../../../../types';
import { useAuth } from '../../../auth/contexts/AuthContext';
import { StarService } from '../../../stars/services/StarService';
import StarGivingModal from '../../../stars/components/StarGivingModal';
import {
  DISMISSAL_META,
  getNextDismissalType,
  resolveStudentDismissal,
} from '../../utils/dismissalProcedure';
import '../../../muses/styles/MuseTiers.css';
import AvatarPickerModal from './AvatarPickerModal';
import { ClassroomAvatarService } from '../../classroom/ClassroomAvatarService';

interface ClassroomStudentChipProps {
  student: User;
  squadId: string;
  variant?: 'tray' | 'member';
  draggable?: boolean;
  /** Explicitly marked absent for the live/current class */
  absent?: boolean;
  onDragStartStudent?: (studentId: string) => void;
  onDragEndStudent?: () => void;
}

function displayNameShort(student: User): string {
  const raw = String(student.displayName || 'Student').trim();
  const parts = raw.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0];
  const last = parts[parts.length - 1];
  return `${parts[0]} ${last.charAt(0)}.`;
}

const ClassroomStudentChip: React.FC<ClassroomStudentChipProps> = ({
  student,
  squadId,
  variant = 'tray',
  draggable = false,
  absent = false,
  onDragStartStudent,
  onDragEndStudent,
}) => {
  const { currentUser } = useAuth();
  const [starsReceived, setStarsReceived] = useState(0);
  const [canReceive, setCanReceive] = useState(true);
  const [photoConsent, setPhotoConsent] = useState<boolean | null>(null);
  const [isStarModalOpen, setIsStarModalOpen] = useState(false);
  const [savingDismissal, setSavingDismissal] = useState(false);
  const [localStudent, setLocalStudent] = useState(student);
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const [savingName, setSavingName] = useState(false);
  const [avatarOpen, setAvatarOpen] = useState(false);
  const [avatarBusy, setAvatarBusy] = useState(false);

  useEffect(() => {
    setLocalStudent(student);
  }, [student]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const info = await StarService.canReceiveMoreStars(student.id, squadId);
        if (!cancelled) {
          setStarsReceived(info.starsReceived);
          setCanReceive(info.canReceive);
        }
      } catch {
        if (!cancelled) {
          setStarsReceived(0);
          setCanReceive(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [student.id, squadId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (!student.parentIds?.length) {
          if (!cancelled) setPhotoConsent(null);
          return;
        }
        const parentSnap = await getDoc(doc(db, 'users', student.parentIds[0]));
        if (!parentSnap.exists()) {
          if (!cancelled) setPhotoConsent(null);
          return;
        }
        const parentData = parentSnap.data() as User;
        const photoRelease = parentData.consents?.photoRelease;
        const accepted =
          photoRelease?.accepted === true || photoRelease?.value === 'accept';
        const hasResponse =
          photoRelease?.accepted !== undefined || photoRelease?.value !== undefined;
        if (!cancelled) setPhotoConsent(hasResponse ? accepted : null);
      } catch {
        if (!cancelled) setPhotoConsent(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [student.parentIds, student.id]);

  const handleDismissalClick = async (event: React.MouseEvent) => {
    event.stopPropagation();
    if (savingDismissal) return;
    const resolved = resolveStudentDismissal(localStudent);
    const nextType = getNextDismissalType(resolved.type);
    setSavingDismissal(true);
    try {
      const patch: Partial<User> = {
        dismissalProcedureType: nextType === 'unassigned' ? null : nextType,
        dismissalProcedureNote:
          nextType === 'special'
            ? resolved.otherText || (localStudent as any)?.dismissalProcedureNote || null
            : null,
      };
      await updateDoc(doc(db, 'users', student.id), patch as any);
      setLocalStudent((prev) => ({ ...prev, ...patch }));
    } catch (e) {
      console.error('Failed to update dismissal', e);
    } finally {
      setSavingDismissal(false);
    }
  };

  const beginNameEdit = (event: React.MouseEvent) => {
    event.stopPropagation();
    setNameDraft(localStudent.displayName || '');
    setEditingName(true);
  };

  const saveName = async () => {
    const displayName = nameDraft.trim();
    if (!displayName) return;
    setSavingName(true);
    try {
      await updateDoc(doc(db, 'users', student.id), { displayName });
      setLocalStudent(prev => ({ ...prev, displayName }));
      setEditingName(false);
    } catch (e) {
      console.error('Failed to update student name', e);
    } finally { setSavingName(false); }
  };

  const saveAvatar = async (url: string, kind: 'muse' | 'upload') => {
    setAvatarBusy(true);
    try {
      await ClassroomAvatarService.setStudent(squadId, student.id, url, kind);
      setLocalStudent(prev => ({ ...prev, classroomAvatarUrl: url, classroomAvatarKind: kind }));
      setAvatarOpen(false);
    } catch (e) {
      console.error('Failed to update student avatar', e);
    } finally { setAvatarBusy(false); }
  };

  const museImg = localStudent.classroomAvatarUrl || (
    localStudent.selectedMuse && typeof localStudent.selectedMuse === 'object'
      ? localStudent.selectedMuse.img
      : undefined
  );
  const museTier = localStudent.selectedMuse?.tier;
  const dismissal = resolveStudentDismissal(localStudent);
  const dismissalMeta = DISMISSAL_META[dismissal.type];

  return (
    <>
      <div
        className={`cd-student-chip cd-student-chip--${variant}${draggable ? ' is-draggable' : ''}${absent ? ' is-absent' : ''}`}
        draggable={draggable}
        title={absent ? 'Marked absent' : undefined}
        onDragStart={(e) => {
          if (!draggable) return;
          e.dataTransfer.setData('text/plain', student.id);
          e.dataTransfer.effectAllowed = 'move';
          onDragStartStudent?.(student.id);
        }}
        onDragEnd={() => onDragEndStudent?.()}
      >
        <div className="cd-student-chip-main">
          <button type="button" className={`cd-student-avatar cd-student-avatar-edit ${museTier ? `student-card-tier-${museTier}` : ''}`} onClick={e => { e.stopPropagation(); setAvatarOpen(true); }} aria-label={`Choose ${localStudent.displayName || 'student'} avatar`}>
            {museImg ? (
              <img
                src={museImg}
                alt=""
                className={museTier ? `muse-tier-${museTier}` : undefined}
              />
            ) : (
              <span className="cd-student-avatar-fallback">
                {(localStudent.displayName || '?').charAt(0).toUpperCase()}
              </span>
            )}
          </button>
          {editingName ? <input
            className="cd-student-name-input"
            value={nameDraft}
            onChange={e => setNameDraft(e.target.value)}
            onBlur={() => { void saveName(); }}
            onClick={e => e.stopPropagation()}
            onKeyDown={e => {
              if (e.key === 'Enter') { e.preventDefault(); void saveName(); }
              if (e.key === 'Escape') { e.preventDefault(); setEditingName(false); }
            }}
            aria-label="Student name"
            disabled={savingName}
            autoFocus
          /> : <button type="button" className="cd-student-name cd-student-name-button" onClick={beginNameEdit} title="Edit student name">
            {displayNameShort(localStudent)} <span aria-hidden="true">✎</span>
          </button>}
        </div>

        <div className="cd-student-meta">
          {photoConsent === true && (
            <span className="cd-meta-icon" title="Photo consent on file">
              📷
            </span>
          )}
          <button
            type="button"
            className={`cd-meta-icon cd-dismissal ${savingDismissal ? 'is-saving' : ''}`}
            onClick={handleDismissalClick}
            title={`Dismissal: ${dismissal.tooltip} Click to change.`}
            aria-label={`Dismissal: ${dismissalMeta.shortLabel}`}
            disabled={savingDismissal}
          >
            {dismissalMeta.emoji}
          </button>
          <div className="cd-star-slots">
            {[0, 1, 2].map((index) => {
              const isEarned = index < starsReceived;
              const isClickable = !isEarned && canReceive;
              return (
                <button
                  key={index}
                  type="button"
                  className={`cd-star-slot ${isEarned ? 'earned' : ''} ${isClickable ? 'clickable' : ''}`}
                  onClick={
                    isClickable
                      ? (e) => {
                          e.stopPropagation();
                          setIsStarModalOpen(true);
                        }
                      : undefined
                  }
                  disabled={!isClickable}
                  title={isEarned ? 'Star earned' : isClickable ? 'Award star' : 'Max stars'}
                >
                  ⭐
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <StarGivingModal
        isOpen={isStarModalOpen}
        onClose={() => setIsStarModalOpen(false)}
        student={localStudent}
        teacherId={currentUser?.uid || ''}
        squadId={squadId}
        onStarAwarded={(success) => {
          if (success) {
            void (async () => {
              try {
                const info = await StarService.canReceiveMoreStars(student.id, squadId);
                setStarsReceived(info.starsReceived);
                setCanReceive(info.canReceive);
              } catch {
                /* ignore */
              }
            })();
          }
        }}
      />
      <AvatarPickerModal
        isOpen={avatarOpen}
        title={`Choose ${localStudent.displayName || 'student'} avatar`}
        busy={avatarBusy}
        onClose={() => setAvatarOpen(false)}
        onMuse={url => { void saveAvatar(url, 'muse'); }}
        onUpload={file => { void (async () => {
          setAvatarBusy(true);
          try {
            const url = await ClassroomAvatarService.upload(squadId, `student-${student.id}`, file);
            await saveAvatar(url, 'upload');
          } finally { setAvatarBusy(false); }
        })(); }}
      />
    </>
  );
};

export default ClassroomStudentChip;
